/**
 * WEB half of the focus-outline helper: it learns whether the last input was the keyboard from document-level listeners,
 * added once for the whole page the first time a control asks. A key press sets it; a pointer press (mouse, pen or touch)
 * clears it. The browser focuses a clicked button AFTER the pointer-down and a Tab-focused one AFTER the key-down, so the
 * flag already holds the right answer when a control's `focused` state turns on.
 */
import { useEffect } from "react";
import type { PressState } from "@rista/ui/library/components/PressableSurface";

import { stateForOutline, type FocusOutline } from "./shared";

export * from "./shared";

/** Whether the last input was the keyboard. Starts false: a control focused before any key press was reached by the pointer. */
let keyboardWasLast = false;

/** Whether the page's listeners are in place already. */
let listening = false;

/** Adds the page's listeners, once. */
function listenToInput(): void {
  if (listening || typeof document === "undefined") {
    return;
  }
  listening = true;
  const onKey = () => {
    keyboardWasLast = true;
  };
  const onPointer = () => {
    keyboardWasLast = false;
  };
  // capture phase, so a control that stops the event still counts
  document.addEventListener("keydown", onKey, true);
  document.addEventListener("pointerdown", onPointer, true);
  document.addEventListener("mousedown", onPointer, true);
  document.addEventListener("touchstart", onPointer, true);
}

/**
 * Answers a function that maps a control's press state to the one its look is drawn from: with `"keyboard"`, `focused` is
 * dropped unless the focus came from the keyboard. Call it in the control and pass each state through the function.
 */
export function useFocusOutline(mode: FocusOutline | undefined): (state: PressState) => PressState {
  useEffect(() => {
    if (mode === "keyboard") {
      listenToInput();
    }
  }, [mode]);
  return (state) => stateForOutline(mode, state, () => keyboardWasLast);
}
